// WP-1.18 (SR-4, S-10): free-text notes on an entity — list, add, edit, delete.
import { useState } from 'react';
import { inputClass, primaryButtonInlineClass } from './FormKit';
import { useCreateNote, useDeleteNote, useNotes, useUpdateNote } from '../lib/notes';

export function NotesSection({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}): React.JSX.Element {
  const { data: notes } = useNotes(entityType, entityId);
  const create = useCreateNote();
  const update = useUpdateNote();
  const remove = useDeleteNote();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');

  const addNote = (): void => {
    const body = draft.trim();
    if (body === '') return;
    create.mutate(
      { entityType, entityId, body },
      {
        onSuccess: () => {
          setDraft('');
        },
      },
    );
  };

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 text-sm font-medium text-ink">Notes</p>

      <ul className="mb-3 space-y-2">
        {(notes ?? []).map((note) => (
          <li key={note.id} className="border-b border-border pb-2 last:border-0">
            {editingId === note.id ? (
              <div className="flex gap-2">
                <input
                  className={`${inputClass} flex-1`}
                  aria-label="Edit note"
                  value={editBody}
                  onChange={(e) => {
                    setEditBody(e.target.value);
                  }}
                />
                <button
                  type="button"
                  className={primaryButtonInlineClass}
                  onClick={() => {
                    const body = editBody.trim();
                    if (body === '') return;
                    update.mutate(
                      { id: note.id, body },
                      {
                        onSuccess: () => {
                          setEditingId(null);
                        },
                      },
                    );
                  }}
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-ink">{note.body}</p>
                <div className="flex shrink-0 gap-2 text-xs">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => {
                      setEditingId(note.id);
                      setEditBody(note.body);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-danger hover:underline"
                    onClick={() => {
                      remove.mutate(note.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="flex gap-2">
        <input
          className={`${inputClass} flex-1`}
          aria-label="New note"
          placeholder="Add a note…"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addNote();
          }}
        />
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={create.isPending}
          onClick={addNote}
        >
          Add
        </button>
      </div>
    </div>
  );
}
